// =============================================================================
// AgendaView - Elenco cronologico degli appuntamenti
// =============================================================================
// Alternativa alla griglia risorse: gli appuntamenti dell'intervallo in ordine
// di orario, raggruppati per giornata dell'ORGANIZZAZIONE (non del browser).
// =============================================================================

import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInMinutes, format, parseISO } from 'date-fns';
import {
  Boxes,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Plus,
  User,
  Users,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useDateLocale } from '../../../hooks/useDateLocale';
import { useTenantTimeZone } from '../../../store/tenantStore';
import { useShowViewerLocalTimes } from '../../../store/schedulerStore';
import {
  browserTimeZone,
  civilDateInTz,
  civilDayToCarrier,
  timeStringInTz,
  todayInTz,
  type CivilDate,
} from '../../../lib/tenantTime';
import { EmptyState } from '../../common';
import { APPOINTMENT_STATUS_COLORS, APPOINTMENT_STATUS_LABEL_KEYS } from '../../../types';
import type { Appointment } from '../../../types';
import { getServiceColor } from '../utils/color.utils';

/** Stato della paginazione, presente solo nella variante "da oggi in poi". */
export interface AgendaPagination {
  /** Appuntamenti totali dal punto di partenza in poi, anche non ancora scaricati. */
  total: number;
  /** Quanti ne sono stati scaricati finora (prima dei filtri). */
  loaded: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
}

interface AgendaViewProps {
  appointments: Appointment[];
  /** Risorse superstiti ai filtri della sidebar; `null` quando nessun filtro e' attivo. */
  resourceFilterIds: string[] | null;
  /** `range` = intervallo delimitato, `upcoming` = elenco aperto sul futuro. */
  variant?: 'range' | 'upcoming';
  pagination?: AgendaPagination;
  onAppointmentClick: (appointmentId: string) => void;
  onNewAppointment: () => void;
}

interface AgendaDay {
  day: CivilDate;
  items: Appointment[];
}

export function AgendaView({
  appointments,
  resourceFilterIds,
  variant = 'range',
  pagination,
  onAppointmentClick,
  onNewAppointment,
}: AgendaViewProps) {
  const { t } = useTranslation('scheduler');
  const timeZone = useTenantTimeZone();
  const dateLocale = useDateLocale();

  // Un appuntamento resta visibile se almeno una delle sue risorse ha superato
  // i filtri: e' lo stesso criterio con cui la griglia disegna le righe.
  const visible = useMemo(() => {
    if (!resourceFilterIds) return appointments;
    const allowed = new Set(resourceFilterIds);
    return appointments.filter((apt) =>
      (apt.allocations ?? []).some((alloc) => allowed.has(alloc.resourceId)),
    );
  }, [appointments, resourceFilterIds]);

  const days = useMemo<AgendaDay[]>(() => {
    const byDay = new Map<CivilDate, Appointment[]>();

    for (const apt of visible) {
      // La giornata di appartenenza e' quella del calendario del tenant: un
      // appuntamento delle 00:30 a Roma non e' "ieri" per chi guarda da Londra.
      const day = civilDateInTz(parseISO(apt.scheduledStart), timeZone);
      const list = byDay.get(day);
      if (list) list.push(apt);
      else byDay.set(day, [apt]);
    }

    return Array.from(byDay.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, items]) => ({
        day,
        items: items.sort(
          (a, b) => parseISO(a.scheduledStart).getTime() - parseISO(b.scheduledStart).getTime(),
        ),
      }));
  }, [visible, timeZone]);

  const today = todayInTz(timeZone);
  const now = Date.now();

  if (days.length === 0) {
    return (
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex items-center justify-center">
          <EmptyState
            icon={CalendarDays}
            title={
              resourceFilterIds
                ? t('agenda.emptyFiltered')
                : variant === 'upcoming'
                  ? t('agenda.emptyUpcoming')
                  : t('agenda.empty')
            }
            description={t('agenda.emptyHint')}
            action={
              <button
                onClick={onNewAppointment}
                className="inline-flex items-center gap-2 px-4 py-2 btn-primary font-medium shadow-sm"
              >
                <Plus className="w-4 h-4" />
                {t('newAppointment')}
              </button>
            }
          />
        </div>
        <AgendaFooter count={0} pagination={pagination} />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto">
        {days.map(({ day, items }) => {
          const isToday = day === today;
          // Nel giorno corrente il separatore "adesso" cade prima del primo
          // appuntamento non ancora iniziato (in fondo se sono tutti passati).
          const nowIndex = isToday
            ? items.findIndex((apt) => parseISO(apt.scheduledStart).getTime() >= now)
            : -1;
          const nowAtEnd = isToday && nowIndex === -1;

          return (
            <section key={day}>
              <header
                className={cn(
                  'sticky top-0 z-10 flex items-center justify-between gap-3 px-4 py-2',
                  'border-b border-gray-200 bg-gray-50/95 backdrop-blur-sm',
                )}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900 capitalize">
                    {format(civilDayToCarrier(day), 'EEEE d MMMM', { locale: dateLocale })}
                  </span>
                  {isToday && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-medium">
                      {t('nav.today')}
                    </span>
                  )}
                </div>
                <span className="text-xs text-gray-500">
                  {t('agenda.appointmentsCount', { count: items.length })}
                </span>
              </header>

              <ul className="divide-y divide-gray-100">
                {items.map((apt, index) => (
                  <Fragment key={apt.id}>
                    {index === nowIndex && <NowMarker label={t('agenda.now')} />}
                    <li>
                      <AgendaRow
                        appointment={apt}
                        timeZone={timeZone}
                        onClick={() => onAppointmentClick(apt.id)}
                      />
                    </li>
                  </Fragment>
                ))}
                {nowAtEnd && <NowMarker label={t('agenda.now')} />}
              </ul>
            </section>
          );
        })}

        {pagination?.hasMore && (
          <div className="flex justify-center px-4 py-4">
            <button
              type="button"
              onClick={pagination.onLoadMore}
              disabled={pagination.isLoadingMore}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200',
                'text-sm font-medium text-gray-700 transition-colors',
                'hover:bg-gray-50 disabled:opacity-60 disabled:cursor-wait',
              )}
            >
              {pagination.isLoadingMore ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              {t('agenda.loadMore')}
            </button>
          </div>
        )}
      </div>

      <AgendaFooter count={visible.length} pagination={pagination} />
    </div>
  );
}

// ===========================================================================
// Riga singola
// ===========================================================================

interface AgendaRowProps {
  appointment: Appointment;
  timeZone: string;
  onClick: () => void;
}

function AgendaRow({ appointment, timeZone, onClick }: AgendaRowProps) {
  const { t } = useTranslation('scheduler');
  const showViewerLocal = useShowViewerLocalTimes();
  const viewerTimeZone = browserTimeZone();

  const start = parseISO(appointment.scheduledStart);
  const end = parseISO(appointment.scheduledEnd);
  const duration = differenceInMinutes(end, start);
  const serviceColor = getServiceColor(appointment.serviceDefinitionId, appointment.serviceColor);
  const isCancelled = appointment.status === 'CANCELLED' || appointment.status === 'NO_SHOW';
  const isGroupEvent = (appointment.maxClients ?? 1) > 1;
  const resourceNames = (appointment.allocations ?? []).map((a) => a.resourceName);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group w-full flex items-stretch gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50',
        isCancelled && 'opacity-60',
      )}
    >
      {/* Orari */}
      <div className="w-20 shrink-0 text-right">
        <div className="text-sm font-semibold text-gray-900 tabular-nums">
          {timeStringInTz(start, timeZone)}
        </div>
        <div className="text-xs text-gray-400 tabular-nums">{timeStringInTz(end, timeZone)}</div>
        {showViewerLocal && (
          <div className="mt-0.5 text-[10px] text-gray-400 tabular-nums">
            {timeStringInTz(start, viewerTimeZone)} {t('timeZone.localSuffix')}
          </div>
        )}
      </div>

      {/* Barra colore servizio */}
      <div
        className="w-1 shrink-0 rounded-full"
        style={{ backgroundColor: serviceColor }}
        aria-hidden
      />

      {/* Dettagli */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={cn('font-medium text-gray-900 truncate', isCancelled && 'line-through')}>
            {appointment.serviceName}
          </span>
          <span
            className={cn(
              'px-2 py-0.5 rounded-full text-[11px] font-medium',
              APPOINTMENT_STATUS_COLORS[appointment.status],
            )}
          >
            {t(APPOINTMENT_STATUS_LABEL_KEYS[appointment.status])}
          </span>
        </div>

        <div className="mt-1 flex items-center gap-x-4 gap-y-1 flex-wrap text-sm text-gray-500">
          {/* Su un evento di gruppo il primo cliente non racconta niente:
              quello che serve a colpo d'occhio e' quanti posti sono occupati. */}
          {isGroupEvent ? (
            <span className="inline-flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              {t('agenda.occupancy', {
                occupied: appointment.participantCount ?? 1,
                max: appointment.maxClients,
              })}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <User className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="truncate">{appointment.customerName || t('internalActivity')}</span>
            </span>
          )}
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-gray-400" />
            {t('agenda.durationMinutes', { count: duration })}
          </span>
          {resourceNames.length > 0 && (
            <span className="inline-flex items-center gap-1.5 min-w-0">
              <Boxes className="w-3.5 h-3.5 text-gray-400 shrink-0" />
              <span className="truncate">{resourceNames.join(' · ')}</span>
            </span>
          )}
        </div>
      </div>

      <ChevronRight className="w-4 h-4 self-center shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
    </button>
  );
}

// ===========================================================================
// Separatore "adesso" e footer
// ===========================================================================

function NowMarker({ label }: { label: string }) {
  return (
    <li className="flex items-center gap-2 px-4 py-1" aria-hidden>
      <span className="w-20 shrink-0 text-right text-[11px] font-semibold uppercase tracking-wide text-emerald-600">
        {label}
      </span>
      <span className="h-px flex-1 bg-emerald-400" />
    </li>
  );
}

function AgendaFooter({
  count,
  pagination,
}: {
  count: number;
  pagination?: AgendaPagination;
}) {
  const { t } = useTranslation('scheduler');
  return (
    <footer className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-sm text-gray-600 flex items-center justify-between gap-4">
      <span>
        <span className="font-medium">{count}</span>{' '}
        {pagination
          ? t('agenda.appointmentsShown', { count })
          : t('agenda.appointmentsInRange', { count })}
      </span>
      {/* Nell'elenco aperto il totale e' noto anche prima di scaricarlo tutto */}
      {pagination && (
        <span className="text-xs text-gray-500">
          {t('agenda.loadedOf', { loaded: pagination.loaded, total: pagination.total })}
        </span>
      )}
    </footer>
  );
}
